import { toZonedTime, format } from 'date-fns-tz';
import { addMinutes, isValid, parse, parseISO } from "date-fns";
import { GLOBALSETTINGS } from "../data/constants";
import { JSONValue } from "../types/Helper";

export const getCurrentESTTime = (): string => {
  const myDate = new Date();
  const estNow = toZonedTime(myDate, GLOBALSETTINGS.ESTTIMEZONE)
  return getEstDateInString(estNow);
};

export const getEstDateBySameDayFormat = (value: Date): string => {
  const dateValue = new Date(value);
  const nowEst = toZonedTime(new Date(), GLOBALSETTINGS.ESTTIMEZONE);

  const isSameDay =
    dateValue.getFullYear() === nowEst.getFullYear() &&
    dateValue.getMonth() === nowEst.getMonth() &&
    dateValue.getDate() === nowEst.getDate();

  return format(value, isSameDay ? GLOBALSETTINGS.TIME_ONLY : GLOBALSETTINGS.DATE_FORMAT_TIME, { timeZone: GLOBALSETTINGS.ESTTIMEZONE });
}

export const getCurrentESTDate = () => {
  const myDate = new Date();
  const estNow = toZonedTime(myDate, GLOBALSETTINGS.ESTTIMEZONE)
  return estNow;
};

export const getCurrentESTDatePlus10Min = () => {
  const myDate = new Date();
  const estNow = toZonedTime(myDate, GLOBALSETTINGS.ESTTIMEZONE)
  const estPlus10 = addMinutes(estNow, 10);
  return estPlus10;
};

export const getEstDateInString = (date: Date) => {
  return format(date, "yyyy-MM-dd'T'HH:mm:ss", { timeZone: GLOBALSETTINGS.ESTTIMEZONE });
}

export const formatIfDate = (s: string) => {
  const trimmed = s.trim();
  if (GLOBALSETTINGS.ISO_PATTERN.test(trimmed)) {
    const iso = parseISO(trimmed);
    if (isValid(iso)) {
      return format(iso, GLOBALSETTINGS.DATE_FORMAT_TIME);
    }
  }

  const fallbackParsed = parse(trimmed, GLOBALSETTINGS.DATE_FORMAT_TIME, new Date());

  if (isValid(fallbackParsed)) {
    return format(fallbackParsed, GLOBALSETTINGS.DATE_FORMAT_TIME);
  }

  return trimmed;
};

export function formatDates(obj: JSONValue): JSONValue {
  if (typeof obj !== 'object' || obj === null) return obj;

  if (Array.isArray(obj)) {
    return obj.map(formatDates);
  }

  const newObj: { [key: string]: JSONValue } = {};

  for (const key in obj) {
    const val = obj[key];

    if (typeof val === 'string') {
      if (val.includes('>>')) {
        const [oldValNode, newValNode] = val.split('>>');
        newObj[key] = `${formatIfDate(oldValNode)} >> ${formatIfDate(newValNode)}`;
      }
      else if (/^\d{4}-\d{2}-\d{2}T/.test(val)) {
        newObj[key] = formatIfDate(val);
      } else {
        newObj[key] = val;
      }
    } else if (typeof val === 'object' && val !== null) {
      newObj[key] = formatDates(val);
    } else {
      newObj[key] = val;
    }
  }

  return newObj;
}

/** SQL/C# null dates often serialize as 0001-01-01; treat those as absent for display. */
export const toDisplayDateOrNull = (
  value: Date | string | null | undefined
): Date | null => {
  if (value == null || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  if (!isValid(date) || date.getFullYear() <= 1) return null;
  return date;
};

/** Relative age of a server timestamp. The API sends EST wall-clock times with no offset,
 *  so the comparison is against "now" in EST, not the browser's local clock. With no offset to
 *  go on, an age that spans a DST change can be off by an hour. */
export function formatTimeAgo(dateStr: string): string {
  const createdDate = new Date(dateStr);
  const nowEst = getCurrentESTDate();
  const diffSeconds = Math.max(0, Math.floor((nowEst.getTime() - createdDate.getTime()) / 1000));

  const [value, unit] =
    diffSeconds < 60 ? [diffSeconds, 'second']
      : diffSeconds < 60 * 60 ? [Math.floor(diffSeconds / 60), 'minute']
        : diffSeconds < 60 * 60 * 24 ? [Math.floor(diffSeconds / (60 * 60)), 'hour']
          : [Math.floor(diffSeconds / (60 * 60 * 24)), 'day'];
  return `${value} ${unit}${value !== 1 ? 's' : ''} ago`;
}